import { Link } from "react-router-dom";
import { currentSeason } from "../../Constants";
import PrizePanel from "../FantasyPage2/PrizePanel";

// In-app Fantasy guide. Content mirrors the real scoring rules in the backend
// (Database/FantasyScoring.cs) so it can't drift like the old Google Doc did.
// Points per kamp = Holdpoint + Spillerpoint + Rollepoint.

const roles: { name: string; desc: string; plus5: string; plus2: string }[] = [
    { name: "IGL", desc: "In-Game Leader", plus5: "Holdet vinder kampen", plus2: "Nederlag, men tæt (< 24 runder)" },
    { name: "AWP", desc: "AWP-spiller", plus5: "> 0,20 AWP-kills pr. runde", plus2: "> 0,10 AWP-kills pr. runde" },
    { name: "HS Machine", desc: "Headshot-maskine", plus5: "HS% ≥ 70", plus2: "HS% ≥ 50" },
    { name: "Entry Fragger", desc: "Åbner runderne", plus5: "≥ 4 first kills", plus2: "≥ 2 first kills" },
    { name: "Stathunter", desc: "Stjernespiller", plus5: "Rating 3.0 > 1,50", plus2: "Rating 3.0 > 1,05" },
    { name: "Noob", desc: "Bevidst dårlig pick", plus5: "Rating 3.0 < 0,70", plus2: "Rating 3.0 < 1,05" },
    { name: "Mr. Consistent", desc: "Stabil hele vejen", plus5: "KAST ≥ 85", plus2: "KAST ≥ 74" },
    { name: "Pistol Warrior", desc: "Pistolrunde-konge", plus5: "≥ 4 pistol-kills", plus2: "≥ 2 pistol-kills" },
    { name: "Multi Killer", desc: "Multi-kill runder", plus5: "≥ 2 runder med 3K+", plus2: "≥ 1 runde med 3K+" },
    { name: "T Attacker", desc: "T-side specialist", plus5: "T-side Rating 3.0 > 1,60", plus2: "T-side Rating 3.0 > 1,00" },
    { name: "CT Defender", desc: "CT-side specialist", plus5: "CT-side Rating 3.0 > 1,60", plus2: "CT-side Rating 3.0 > 1,05" },
];

const FantasyGuidePage = () => {
    return (
        <div className="container text-white" style={{ maxWidth: 900, paddingTop: 24, paddingBottom: 48 }}>
            <h1 className="mb-2">Fantasy Guide &amp; Point-system</h1>
            <p style={{ color: "rgba(255,255,255,0.6)" }}>
                Point pr. kamp = <strong>Holdpoint</strong> + <strong>Spillerpoint</strong> + <strong>Rollepoint</strong>.
                Alle ratings er <strong>Rating 3.0</strong> (falder tilbage til 2.1/2.0 for kampe uden 3.0-data).
            </p>

            <div className="mt-4">
                <PrizePanel season={currentSeason} />
            </div>

            <h3 className="mt-4">📋 Regler</h3>
            <ul>
                <li><strong>5 spillere</strong> — hver med sin egen rolle (samme rolle kan ikke bruges to gange).</li>
                <li>
                    <strong style={{ color: "#f0ad4e" }}>Maks. 2 spillere fra samme hold.</strong> Vælger du 3 eller
                    flere fra samme klub, bliver dit fantasy hold <strong>fjernet fra ranglisten</strong> — holdet og
                    dine point bliver, men det tæller ikke med i konkurrencen. Du kan altid rette det ved at bytte en
                    spiller ud, så længe tilmeldingen er åben.
                </li>
                <li><strong>Budget på 1.000.000 kr.</strong> — de 5 spilleres priser skal holde sig inden for det.</li>
                <li>Du kan have <strong>ét hold pr. division</strong>, og du kan redigere det frit indtil deadline.</li>
            </ul>

            <h3 className="mt-4">🏆 Holdpoint</h3>
            <ul>
                <li>Holdet vinder kampen: <strong style={{ color: "#5cb85c" }}>+6</strong></li>
                <li>Holdet taber kampen: <strong style={{ color: "#d9534f" }}>−3</strong></li>
            </ul>

            <h3 className="mt-4">🎯 Spillerpoint</h3>
            <p style={{ color: "rgba(255,255,255,0.6)" }}>Lægges sammen for hver kamp:</p>
            <ul>
                <li><strong>Rating 3.0</strong> — over gennemsnittet (≥ 1,15) giver point, under trækker fra</li>
                <li><strong>K/D</strong> — over 1,0 giver point, under trækker fra</li>
                <li><strong>KAST</strong> — {">"} 80: +5 · {">"} 75: +2 · {">"} 70: +1 · {"<"} 70: −1 · {"<"} 65: −2 · {"<"} 50: −5</li>
                <li><strong>First kills</strong> — +1 pr. first kill</li>
                <li><strong>ADR</strong> — +1 for hver 5 ADR over 75 (og −1 for hver 5 under)</li>
            </ul>

            <h3 className="mt-4">⭐ Rollepoint</h3>
            <p style={{ color: "rgba(255,255,255,0.6)" }}>
                Hver spiller har én rolle. Rollerne er balanceret, så en <strong>+5</strong> er lige svær at
                opnå uanset rolle (ca. top 15% af kampene). Opfyldes ingen af betingelserne: <strong style={{ color: "#d9534f" }}>−2</strong>.
            </p>
            <div style={{ overflowX: "auto" }}>
                <table className="table table-dark table-sm" style={{ borderRadius: 8, overflow: "hidden" }}>
                    <thead>
                        <tr>
                            <th>Rolle</th>
                            <th>Beskrivelse</th>
                            <th style={{ color: "#5cb85c" }}>+5</th>
                            <th style={{ color: "#f0ad4e" }}>+2</th>
                        </tr>
                    </thead>
                    <tbody>
                        {roles.map((r) => (
                            <tr key={r.name}>
                                <td><strong>{r.name}</strong></td>
                                <td style={{ color: "rgba(255,255,255,0.6)" }}>{r.desc}</td>
                                <td>{r.plus5}</td>
                                <td>{r.plus2}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <h3 className="mt-4">💰 Priser</h3>
            <p style={{ color: "rgba(255,255,255,0.6)" }}>
                En spillers pris beregnes ud fra sidste sæsons præstation: <strong>Rating 3.0</strong>, K/D,
                KAST, ADR og opening duels. Prisen justeres desuden op/ned hvis spilleren skifter division
                (±5% pr. divisionsniveau).
            </p>

            <div className="mt-4">
                <Link to="/fantasy" className="btn btn-outline-light btn-sm">← Tilbage til Fantasy</Link>
            </div>
        </div>
    );
};

export default FantasyGuidePage;
